<template>
  <FullPage class="contacts" @contextmenu.prevent>
    <BaseIntegrationMissing
      v-if="hasContacts !== 'synchronized'"
      class="integration-missing"
      feature="contacts"
      :synchronizing="hasContacts === 'initializing'"
    />
    <template v-else>
      <BaseNumberedHeader title="Contacts" :number="totalContacts" />
      <div class="search-bar">
        <BaseInput
          ref="searchInputRef"
          v-model:value="searchInput"
          placeholder="Search contacts..."
          has-styles
          class="search-input"
        >
          <template #left>
            <SearchIcon class="search-icon" />
          </template>
        </BaseInput>
      </div>
      <Suspense :key="searchQuery">
        <ContactsList
          class="list"
          :search-query="searchQuery"
          @update:total="totalContacts = $event"
        />
        <template #fallback>
          <Loader class="loader">
            <BaseSkeletonList />
          </Loader>
        </template>
      </Suspense>
    </template>
  </FullPage>
</template>

<script lang="ts">
import { debounce } from "lodash-es";
import { defineComponent, onMounted, ref, watch } from "vue";

import BaseInput from "@/components/base/BaseInput.vue";
import BaseIntegrationMissing from "@/components/base/BaseIntegrationMissing.vue";
import BaseNumberedHeader from "@/components/base/BaseNumberedHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import ContactsList from "@/components/contacts/ContactsList.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import FullPage from "@/components/templates/FullPage.vue";

export default defineComponent({
  name: "Contacts",
  components: {
    BaseIntegrationMissing,
    BaseInput,
    BaseNumberedHeader,
    BaseSkeletonList,
    ContactsList,
    FullPage,
    SearchIcon,
    Loader,
  },
  props: {
    // Organization scope, set by the org-scoped route. Contacts are
    // personal-workspace only today; reserved for per-org contacts support.
    orgId: { type: String, required: false },
  },
  setup() {
    const hasContacts = inject.hasContacts();
    const searchInput = ref("");
    const searchQuery = ref("");
    const totalContacts = ref(0);
    const searchInputRef = ref<InstanceType<typeof BaseInput>>();

    const debouncedUpdateSearch = debounce((value: string) => {
      searchQuery.value = value;
    }, 500);

    watch(searchInput, (newValue) => {
      debouncedUpdateSearch(newValue);
    });

    onMounted(() => {
      searchInputRef.value?.focus();
    });

    return {
      hasContacts,
      searchInput,
      searchQuery,
      totalContacts,
      searchInputRef,
    };
  },
});
</script>

<style scoped lang="scss">
.contacts {
  .integration-missing {
    flex: 1;
    min-height: 200px;
  }

  .search-bar {
    display: flex;
    align-items: center;
    width: 100%;

    .search-input {
      width: 100%;
      @extend %font-body-regular;
    }

    :deep(svg) {
      width: 16px;
      height: 16px;
      margin-top: 4px;
    }

    @include themed() {
      :deep(svg path) {
        stroke: t($grey-40);
      }
    }
  }

  .list {
    overflow-y: scroll;
    height: 100%;
    margin-inline-start: -64px;
    margin-inline-end: -64px;
    margin-bottom: 16px;
  }

  .loading-fallback {
    padding: 0 64px;
    margin-inline-start: -64px;
    margin-inline-end: -64px;
    margin-bottom: 16px;
  }
}
</style>
